import { Environment } from '@cobliteam/react-environments';
import type { Camelized } from '@cobliteam/utils/camelize';
import { infiniteQueryOptions, mutationOptions, type QueryClient, queryOptions, skipToken } from '@tanstack/react-query';
import type { z } from 'zod/v4';
import {
  type CoachingFeedbackAction,
  CoachingFeedbackDetailsDocument,
  type CoachingFeedbackDetailsQuery,
  type CoachingFeedbackInput,
  CoachingFeedbackPageDocument,
  type CoachingFeedbackPageQuery,
  type CoachingFeedbackStatus,
  type CoachingFileType,
} from '../__generated__/graphql';
import { CoachingFeedback, UploadCoachingFileDTO } from '../__generated__/rest/poolerCoaching.zod';
import { requestGraphQL } from '../client/graphql';
import { requestHttp } from '../client/http';

export type { CoachingFeedbackAction, CoachingFeedbackInput, CoachingFeedbackStatus, CoachingFileType } from '../__generated__/graphql';

/** Página de Tratativas como o janus devolve, em camelCase. */
export type CoachingFeedbackPage = NonNullable<Camelized<CoachingFeedbackPageQuery>['coachingFeedback']>;

/** Uma Tratativa da lista: só os campos que o `coachingFeedbackPage` seleciona. */
export type CoachingFeedbackListItem = NonNullable<NonNullable<CoachingFeedbackPage['content']>[number]>;

export type CoachingFeedbackDetails = NonNullable<Camelized<CoachingFeedbackDetailsQuery>['coachingFeedbackDetails']>;

// A Mídia vem como o janus devolve: o módulo não tem o `toMediaView` do api-lib.
export type CoachingFeedbackEvidence = NonNullable<NonNullable<CoachingFeedbackDetails['coachingEvidences']>[number]>;

export type CoachingEvidenceFile = NonNullable<NonNullable<CoachingFeedbackEvidence['files']>[number]>;

/** O fio do janus e do pooler, em snake_case: o formato que os mocks do e2e e da demo precisam seguir. */
export type CoachingFeedbackPageWire = NonNullable<CoachingFeedbackPageQuery['coachingFeedback']>;
export type CoachingFeedbackDetailsWire = NonNullable<CoachingFeedbackDetailsQuery['coachingFeedbackDetails']>;
export type PoolerCoachingFeedbackWire = z.input<typeof CoachingFeedback>;

export type CreateCoachingFeedbackInput = { driverId: string; mediaIds: string[] };

/** A Tratativa como o pooler devolve na criação e na edição, em camelCase. */
export type CreatedCoachingFeedback = Camelized<z.output<typeof CoachingFeedback>>;

/** Só os campos definidos vão ao `PATCH`; `mediaIds` é a lista inteira das evidências que ficam. */
export type UpdateCoachingFeedbackInput = {
  coachingFeedbackId: string;
  mediaIds?: string[];
  status?: CoachingFeedbackStatus;
  note?: string;
  feedbackAction?: CoachingFeedbackAction;
  driverId?: string;
};

export type UploadEvidenceFileInput = {
  coachingFeedbackId: string;
  coachingEvidenceId: string;
  /** A imagem capturada do vídeo, que também serve de prévia enquanto o envio não termina. */
  blobUrl: string;
  type: CoachingFileType;
  timestamp?: number;
};

export type DeleteEvidenceFileInput = { coachingFeedbackId: string; coachingEvidenceId: string; evidenceFileId: string };

const INFINITE_PAGE_SIZE = 10;

const poolerHost = () => Environment.getInstance().getEnvironment().apiHost;

// Keys do api-lib que ainda leem Tratativa: a Mídia mostra as Tratativas em que é evidência.
const LEGACY_MEDIA_VIEW_KEY = ['CAMERA-MEDIA', 'VIEW'] as const;

const fetchPage = async (input: CoachingFeedbackInput, signal: AbortSignal): Promise<CoachingFeedbackPage | null> => {
  const { coachingFeedback } = await requestGraphQL(CoachingFeedbackPageDocument, { input }, { signal });
  return coachingFeedback ?? null;
};

export const coachingFeedbackQueries = {
  all: () => ['coachingFeedback'] as const,

  /** Uma página da lista. Sem `input`, a query fica desligada. */
  page: (input?: CoachingFeedbackInput) =>
    queryOptions({
      queryKey: [...coachingFeedbackQueries.all(), 'page', input] as const,
      queryFn: input ? ({ signal }) => fetchPage(input, signal) : skipToken,
      // O default do app é false; a lista volta a buscar quando a tela monta, como no hook legado.
      refetchOnMount: true,
    }),

  /** A Tratativa completa. Sem `id`, a query fica desligada; o 404 resolve `null`. */
  detail: (id?: string) =>
    queryOptions({
      queryKey: [...coachingFeedbackQueries.all(), 'detail', id] as const,
      queryFn: id
        ? async ({ signal }) => {
            const { coachingFeedbackDetails } = await requestGraphQL(CoachingFeedbackDetailsDocument, { id }, { signal, nullOnNotFound: true });
            return coachingFeedbackDetails ?? null;
          }
        : skipToken,
    }),

  /** A lista em páginas de 10, pela ordem do backend. Sem `input`, a query fica desligada. */
  infinite: (input?: Omit<CoachingFeedbackInput, 'page' | 'size'>) =>
    infiniteQueryOptions({
      queryKey: [...coachingFeedbackQueries.all(), 'infinite', input] as const,
      queryFn: input ? ({ pageParam, signal }) => fetchPage({ ...input, page: pageParam, size: INFINITE_PAGE_SIZE }, signal) : skipToken,
      initialPageParam: 0,
      getNextPageParam: (lastPage, pages) => (lastPage?.last === false ? pages.length : undefined),
    }),
};

type DetailSnapshot = { previous: CoachingFeedbackDetails | null | undefined };

// Cancela a busca em voo do detalhe, para ela não sobrescrever a escrita otimista, e guarda o anterior para o rollback.
const writeDetail = async (client: QueryClient, coachingFeedbackId: string, write: (details: CoachingFeedbackDetails) => CoachingFeedbackDetails): Promise<DetailSnapshot> => {
  const { queryKey } = coachingFeedbackQueries.detail(coachingFeedbackId);
  await client.cancelQueries({ queryKey });
  const previous = client.getQueryData(queryKey);
  client.setQueryData(queryKey, (details) => (details ? write(details) : details));
  return { previous };
};

const restoreDetail = (client: QueryClient, coachingFeedbackId: string, snapshot: DetailSnapshot | undefined) => {
  if (snapshot?.previous !== undefined) client.setQueryData(coachingFeedbackQueries.detail(coachingFeedbackId).queryKey, snapshot.previous);
};

// A resposta do pooler não tem o formato do detalhe, então a Tratativa volta pelo janus. Só a última escrita pendente
// busca de novo: com o salvamento automático, a resposta de uma escrita antiga desfaria o otimista da seguinte.
const refetchWhenIdle = (client: QueryClient) => {
  if (client.isMutating({ mutationKey: coachingFeedbackQueries.all() }) > 1) return;
  void client.invalidateQueries({ queryKey: coachingFeedbackQueries.all(), refetchType: 'all' });
};

const mapEvidences = (details: CoachingFeedbackDetails, coachingEvidenceId: string, map: (files: CoachingEvidenceFile[]) => CoachingEvidenceFile[]) => ({
  ...details,
  coachingEvidences: details.coachingEvidences?.map((evidence) =>
    evidence?.id === coachingEvidenceId ? { ...evidence, files: map((evidence.files ?? []).filter((file): file is CoachingEvidenceFile => !!file)) } : evidence
  ),
});

export const coachingFeedbackMutations = {
  create: () =>
    mutationOptions({
      mutationKey: [...coachingFeedbackQueries.all(), 'create'] as const,
      mutationFn: ({ driverId, mediaIds }: CreateCoachingFeedbackInput): Promise<CreatedCoachingFeedback> =>
        requestHttp({ method: 'POST', host: poolerHost(), path: '/coaching/feedback', body: { driver_id: driverId, media_ids: mediaIds }, schema: CoachingFeedback }),
      // Sem esperar as listas: quem cria navega para a Tratativa nova, e o `mutateAsync` não segura essa navegação.
      onSuccess: (_data, _input, _onMutateResult, { client }) => {
        void client.invalidateQueries({ queryKey: coachingFeedbackQueries.all(), refetchType: 'all' });
        // Ponte reversa até a Mídia migrar (ADR-0021): o legado só invalidava as ativas, e segue assim.
        void client.invalidateQueries({ queryKey: LEGACY_MEDIA_VIEW_KEY });
      },
    }),

  /**
   * Edita a Tratativa com escrita otimista no detalhe e rollback no erro. `authorName` é quem aparece como autor da
   * anotação e da última edição até o detalhe voltar do janus.
   */
  update: ({ authorName }: { authorName?: string } = {}) =>
    mutationOptions({
      mutationKey: [...coachingFeedbackQueries.all(), 'update'] as const,
      mutationFn: ({ coachingFeedbackId, mediaIds, status, note, feedbackAction, driverId }: UpdateCoachingFeedbackInput): Promise<CreatedCoachingFeedback> =>
        requestHttp({
          method: 'PATCH',
          host: poolerHost(),
          path: `/coaching/feedback/${coachingFeedbackId}`,
          body: { media_ids: mediaIds, status, note, feedback_action: feedbackAction, driver_id: driverId },
          schema: CoachingFeedback,
        }),
      onMutate: ({ coachingFeedbackId, mediaIds, status, note, feedbackAction }, { client }) =>
        writeDetail(client, coachingFeedbackId, (details) => {
          const now = new Date().toISOString();
          const author = { fullName: authorName };
          const coachingEvidences = mediaIds
            ? mediaIds.map(
                (mediaId) =>
                  details.coachingEvidences?.find((evidence) => evidence?.mediaId === mediaId) ?? {
                    id: crypto.randomUUID(),
                    mediaId,
                    safetyOccurrences: [],
                    files: [],
                    media: null,
                  }
              )
            : details.coachingEvidences;
          return {
            ...details,
            coachingEvidences,
            ...(status && { status }),
            ...(feedbackAction && { feedbackAction }),
            ...(typeof note === 'string' && { note, noteAuthor: author, noteCreatedAt: now }),
            updatedAt: now,
            updatedByUser: author,
          };
        }),
      onError: (_error, { coachingFeedbackId }, snapshot, { client }) => restoreDetail(client, coachingFeedbackId, snapshot),
      onSettled: (_data, _error, _input, _snapshot, { client }) => refetchWhenIdle(client),
    }),

  /** Envia a imagem capturada do vídeo; ela aparece na evidência na hora, pela `blobUrl`, até o detalhe voltar. */
  uploadEvidenceFile: () =>
    mutationOptions({
      mutationKey: [...coachingFeedbackQueries.all(), 'uploadEvidenceFile'] as const,
      mutationFn: async ({ coachingEvidenceId, blobUrl, type, timestamp }: UploadEvidenceFileInput) => {
        const file = new Blob([await fetch(blobUrl).then((response) => response.arrayBuffer())], { type: 'image/png' });
        const body = new FormData();
        body.append('file', file, 'capture.png');
        body.append('type', type);
        // Captura no segundo 0 é um instante válido.
        if (timestamp !== undefined) body.append('fileTimestamp', String(timestamp));
        return requestHttp({ method: 'PUT', host: poolerHost(), path: `/coaching/feedback/evidence/${coachingEvidenceId}/file`, body, schema: UploadCoachingFileDTO });
      },
      onMutate: ({ coachingFeedbackId, coachingEvidenceId, blobUrl, type, timestamp }, { client }) =>
        writeDetail(client, coachingFeedbackId, (details) =>
          mapEvidences(details, coachingEvidenceId, (files) => [
            ...files,
            { id: crypto.randomUUID(), fileUrl: blobUrl, fileType: type, fileTimestamp: timestamp, createdAt: new Date().toISOString() },
          ])
        ),
      onError: (_error, { coachingFeedbackId }, snapshot, { client }) => restoreDetail(client, coachingFeedbackId, snapshot),
      onSettled: (_data, _error, _input, _snapshot, { client }) => refetchWhenIdle(client),
    }),

  deleteEvidenceFile: () =>
    mutationOptions({
      mutationKey: [...coachingFeedbackQueries.all(), 'deleteEvidenceFile'] as const,
      mutationFn: ({ coachingEvidenceId, evidenceFileId }: DeleteEvidenceFileInput) =>
        requestHttp<void>({ method: 'DELETE', host: poolerHost(), path: `/coaching/feedback/evidence/${coachingEvidenceId}/file/${evidenceFileId}` }),
      onMutate: ({ coachingFeedbackId, coachingEvidenceId, evidenceFileId }, { client }) =>
        writeDetail(client, coachingFeedbackId, (details) => mapEvidences(details, coachingEvidenceId, (files) => files.filter((file) => file.id !== evidenceFileId))),
      onError: (_error, { coachingFeedbackId }, snapshot, { client }) => restoreDetail(client, coachingFeedbackId, snapshot),
      onSettled: (_data, _error, _input, _snapshot, { client }) => refetchWhenIdle(client),
    }),

  remove: () =>
    mutationOptions({
      mutationKey: [...coachingFeedbackQueries.all(), 'remove'] as const,
      mutationFn: (coachingFeedbackId: string) => requestHttp<void>({ method: 'DELETE', host: poolerHost(), path: `/coaching/feedback/${coachingFeedbackId}` }),
      // `all`, e não só as ativas: com o refetchOnMount false do app, uma lista inativa voltaria mostrando a Tratativa excluída.
      onSuccess: (_data, _coachingFeedbackId, _onMutateResult, { client }) => client.invalidateQueries({ queryKey: coachingFeedbackQueries.all(), refetchType: 'all' }),
    }),
};
